import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { mediaUrl } from "@/lib/api";
import Reveal from "@/components/shared/Reveal";
import { useSettings } from "@/components/layout/PublicLayout";
import Seo from "@/components/shared/Seo";

const Articles = () => {
  const settings = useSettings();
  const tx = settings?.pages?.articles || {};
  const { data: articles = [], isLoading } = useQuery({ queryKey: ["articles"], queryFn: async () => (await api.get("/articles")).data });
  const { data: editions = [] } = useQuery({ queryKey: ["editions"], queryFn: async () => (await api.get("/editions")).data });
  const edYear = (id) => editions.find((e) => e.id === id)?.year;
  const themes = Array.from(new Set(articles.map((a) => a.theme).filter(Boolean)));
  const [theme, setTheme] = useState("all");
  const edKey = (id) => { const e = editions.find((x) => x.id === id); if (!e) return -Infinity; return e.date_iso ? new Date(e.date_iso).getTime() : (e.year ? new Date(e.year, 0, 1).getTime() : -Infinity); };
  const parseDate = (d) => { const t = Date.parse(d); return isNaN(t) ? 0 : t; };
  const shown = articles
    .filter((a) => theme === "all" || a.theme === theme)
    .slice()
    .sort((a, b) => {
      const ek = edKey(b.edition_id) - edKey(a.edition_id);
      if (ek) return ek;
      const ord = (a.order || 0) - (b.order || 0);
      if (ord) return ord;
      return parseDate(b.date) - parseDate(a.date);
    });

  return (
    <>
      <Seo title={tx.seo_title || "Expertises & retours d'expérience — FACADES2build"} description={tx.seo_description || "Articles, retours d'expérience et expertises issus des éditions FACADES2build."} />
      <section className="bg-f2b-black pt-40 pb-16">
        <div className="f2b-container">
          <Reveal><p className="f2b-kicker text-f2b-cyan mb-4">{tx.kicker || "Plateforme éditoriale"}</p></Reveal>
          <Reveal delay={0.08}><h1 className="font-display font-bold text-white text-4xl md:text-6xl leading-tight max-w-3xl">{tx.title || "Expertises & retours d'expérience"}</h1></Reveal>
          <Reveal delay={0.16}><p className="text-white/70 mt-6 max-w-2xl">{tx.lead || "Les conférences et projets présentés à FACADES2build, prolongés en articles de fond."}</p></Reveal>
        </div>
      </section>

      <section className="bg-f2b-light border-b border-f2b-filet py-4">
        <div className="f2b-container flex flex-wrap gap-2">
          <button onClick={() => setTheme("all")} className={`px-4 py-2 text-sm border ${theme === "all" ? "bg-f2b-black text-white border-f2b-black" : "bg-white text-f2b-gray border-f2b-filet hover:border-f2b-cyan"}`}>Tout</button>
          {themes.map((t) => (
            <button key={t} onClick={() => setTheme(t)} className={`px-4 py-2 text-sm border ${theme === t ? "bg-f2b-black text-white border-f2b-black" : "bg-white text-f2b-gray border-f2b-filet hover:border-f2b-cyan"}`}>{t}</button>
          ))}
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="f2b-container">
          {isLoading ? <p className="text-f2b-gray">Chargement…</p> : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
              {shown.map((a, i) => (
                <Reveal key={a.id} delay={i * 0.06}>
                  <Link to={`/blog/${a.slug}`} className="group block" data-testid={`article-card-${i}`}>
                    <div className="aspect-[3/2] overflow-hidden mb-5 bg-f2b-light">
                      {mediaUrl(a.image) && <img src={mediaUrl(a.image)} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                    </div>
                    <div className="flex items-center gap-3 text-xs mb-2">
                      <span className="text-f2b-cyan uppercase tracking-wider">{a.theme}</span>
                      {edYear(a.edition_id) && <span className="text-f2b-gray uppercase tracking-wider">Édition {edYear(a.edition_id)}</span>}
                      <span className="text-f2b-gray">{a.date}</span>
                    </div>
                    <h3 className="font-display font-bold text-f2b-black text-xl leading-tight group-hover:text-f2b-cyan transition-colors">{a.title}</h3>
                    <p className="text-f2b-text/70 text-sm mt-3 line-clamp-3">{a.chapo}</p>
                    {a.company && <p className="text-f2b-gray text-xs mt-3">{a.company}</p>}
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Articles;
